import React, { useRef } from "react"
import { useNavigate } from "react-router-dom"
import { reportShowPath } from "@/QuarterlyReportApp"
import { ArrowLeft, Upload, Eye } from "lucide-react"
import { ReportSection } from "~/api/communications"
import {
  useCapitalStatements,
  useUploadCapitalStatement,
} from "~/api/quarterly_reports"
import { useFileUpload } from "../hooks/useFileUpload"
import { Button } from "@/ui/Button"
import QuarterlyReportLayout from "./QuarterlyReportLayout"
import { useCommunicationContext } from "./CommunicationContext"
import { useQuarterlyReportContext } from "./useQuarterlyReportContext"

const LpCapitalStatement: React.FC = () => {
  const { communicationId } = useCommunicationContext()
  const { reportId, report, isLoading, error } = useQuarterlyReportContext()
  const navigate = useNavigate()
  const uploadingEntityIdRef = useRef<string | null>(null)

  const {
    data: capitalStatementsData,
    isLoading: isLoadingStatements,
    error: statementsError,
  } = useCapitalStatements(reportId)

  const { mutateAsync: uploadCapitalStatement, isLoading: isUploading } =
    useUploadCapitalStatement()

  const {
    uploading,
    error: uploadError,
    FileInput,
  } = useFileUpload({
    acceptedTypes: [".pdf"],
    maxSizeBytes: 50 * 1024 * 1024,
    onUpload: async (signedId: string) => {
      const entityId = uploadingEntityIdRef.current
      if (!entityId) return

      await uploadCapitalStatement({
        reportId,
        investorEntityId: entityId,
        signedId,
      })
    },
    onError: (error) => {
      console.error("Upload failed:", error)
      alert(`Failed to upload file: ${error.message}`)
    },
  })

  const handleUploadClick = (entityId: string) => {
    uploadingEntityIdRef.current = entityId
    const input = document.getElementById(
      "capital-statement-upload",
    ) as HTMLInputElement
    input?.click()
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading LP Capital Statement...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Failed to load report: {error.message}
      </div>
    )
  }

  if (!report) {
    return (
      <div className="alert alert-warning" role="alert">
        Report not found
      </div>
    )
  }

  const entities = capitalStatementsData?.entities || []
  const missingEntities = capitalStatementsData?.missing_entities || []

  return (
    <QuarterlyReportLayout
      title="LP Capital Statement"
      subtitle={`${report.household_name}`}
      actions={
        <Button
          size="md"
          variant="outline"
          icon={<ArrowLeft size={18} />}
          onClick={() =>
            navigate(reportShowPath({ communicationId, reportId }))
          }
        >
          Back to Report
        </Button>
      }
      breadcrumbProps={{
        householdName: report.household_name,
        householdPath: reportShowPath({ communicationId, reportId }),
        sectionName: ReportSection.LP_CAPITAL_STATEMENT,
      }}
    >
      <FileInput id="capital-statement-upload" />

      {uploadError && (
        <div className="alert alert-danger" role="alert">
          {uploadError}
        </div>
      )}

      {statementsError && (
        <div className="alert alert-danger" role="alert">
          Failed to load capital statements: {statementsError.message}
        </div>
      )}

      {missingEntities.length > 0 && (
        <div className="missing-entities-warning">
          <p>
            We could not find a matching capital statement for the following
            entities:
          </p>
          <ul>
            {missingEntities.map((entity) => (
              <li key={entity.id}>{entity.name}</li>
            ))}
          </ul>
        </div>
      )}

      {isLoadingStatements ? (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading capital statements...</p>
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Entities</th>
                <th></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entities.length === 0 ? (
                <tr>
                  <td colSpan={3}>
                    No entities with QPCS exposure found for this household.
                  </td>
                </tr>
              ) : (
                entities.map((entity) => {
                  const isUploadingThis =
                    (uploading || isUploading) &&
                    uploadingEntityIdRef.current === entity.id

                  return (
                    <tr key={entity.id}>
                      <td>{entity.name}</td>
                      <td>
                        {entity.document?.file_url ? (
                          <a
                            href={entity.document.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {entity.document.filename}
                          </a>
                        ) : null}
                      </td>
                      <td>
                        <div className="table-actions">
                          {!entity.document && !isUploadingThis && (
                            <span className="no-file-text">no file chosen</span>
                          )}

                          {entity.document?.file_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                window.open(entity.document!.file_url, "_blank")
                              }
                              icon={<Eye size={18} />}
                            >
                              View
                            </Button>
                          )}

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUploadClick(entity.id)}
                            disabled={uploading || isUploading}
                            loading={isUploadingThis}
                            loadingText="Uploading..."
                            icon={
                              !isUploadingThis ? (
                                <Upload size={18} />
                              ) : undefined
                            }
                          >
                            {entity.document ? "Replace" : "Upload"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </QuarterlyReportLayout>
  )
}

export default LpCapitalStatement
